import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LockClosedIcon, LockOpenIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import useApi from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';

const backgroundUrl = 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=1500&q=80';

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    emailOrMobile: '',
    password: ''
  });
  const [inputError, setInputError] = useState('');
  const { loading, error, fetchData } = useApi();
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check for remembered email on component mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, emailOrMobile: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  const validateEmailOrMobile = (value) => {
    const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    const mobileRegex = /^\+?[1-9]\d{9,14}$/; // Accepts +countrycode and 10-15 digits
    if (emailRegex.test(value)) return true;
    if (mobileRegex.test(value)) return true;
    return false;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (e.target.name === 'emailOrMobile') {
      setInputError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmailOrMobile(formData.emailOrMobile)) {
      setInputError('Please enter a valid email or mobile number.');
      return;
    }
    try {
      const data = await fetchData('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      
      // Use context login to update user and token
      if (data.token) {
        login(data.token, data);
      }
      // Handle remember me
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', formData.emailOrMobile);
        // Store user data with a longer expiration (30 days)
        const userData = {
          ...data,
          expiresAt: new Date().getTime() + (30 * 24 * 60 * 60 * 1000) // 30 days
        };
        localStorage.setItem('user', JSON.stringify(userData));
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      // Redirect based on user role
      if (data.isAdmin) {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden">
      {/* Background image */}
      <img
        src={backgroundUrl}
        alt="Background"
        className="absolute inset-0 w-full h-full object-cover z-0"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 z-0" />
      {/* Glassmorphic Card */}
      <div className="relative z-10 max-w-md w-full mx-auto rounded-3xl shadow-2xl backdrop-blur-md bg-white/30 border border-white/30 p-8 flex flex-col items-center">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-pink-600 mb-2 tracking-wide">theVastra.store</h1>
        <h2 className="text-2xl font-bold text-white mb-2 text-center drop-shadow">Sign in to your account</h2>
        <p className="mb-6 text-center text-sm text-gray-100">
          OR{' '}
          <Link to="/register" className="font-medium text-white hover:text-white underline">
            create a new account
          </Link>
        </p>
        {error && (
          <div className="w-full p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">
            {error}
          </div>
        )}
        <form className="w-full space-y-5" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <input
              id="emailOrMobile"
              name="emailOrMobile"
              type="text"
              autoComplete="username"
              required
              value={formData.emailOrMobile}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 bg-white/80 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow"
              placeholder="Email or Mobile Number"
            />
            {inputError && (
              <div className="text-red-600 text-sm mb-2">{inputError}</div>
            )}
            <div className="relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-300 bg-white/80 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-pink-400 text-base shadow"
                placeholder="Password"
              />
              <button
                type="button"
                tabIndex={-1}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pink-500 focus:outline-none"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <LockOpenIcon className="h-5 w-5" />
                ) : (
                  <LockClosedIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center text-gray-100 cursor-pointer">
              <div className="relative">
                <input 
                  type="checkbox" 
                  className="sr-only"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-colors duration-200 ${
                  rememberMe ? 'border-pink-500 bg-pink-500' : 'border-gray-300'
                }`}>
                  {rememberMe && (
                    <CheckIcon className="w-3 h-3 text-white" />
                  )}
                </div>
              </div>
              <span className="ml-2">Remember me</span>
            </label>
            <Link to="/forgot-password" className="text-white hover:text-white underline">Forgot password?</Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-500 to-indigo-500 text-white font-semibold text-lg shadow hover:from-pink-600 hover:to-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login; 